import { httpClient } from '../http';
import {
  BackendUser,
  UserResponse,
  UsersResponse,
  CreateUserData,
  UpdateUserData,
  ChangePasswordData
} from '@/types/user';

export const userService = {
  // Get all users
  async getUsers(): Promise<BackendUser[]> {
    try {
      const response = await httpClient.get<UsersResponse>('/auth/users/');
      
      if (response.data.success) {
        // Handle both array response and paginated response
        return Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data.users || [];
      } else {
        throw new Error(response.data.message || 'Failed to fetch users');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      
      // Handle different response formats for backward compatibility
      if (error.response?.data) {
        const data = error.response.data;
        
        // If it's a direct array (no wrapper)
        if (Array.isArray(data)) {
          return data;
        }
        
        // If it has a results field (Django pagination)
        if (data.results && Array.isArray(data.results)) {
          return data.results;
        }
        
        // If it has a users field
        if (data.users && Array.isArray(data.users)) {
          return data.users;
        }
      }
      
      throw error;
    }
  },

  // Get single user
  async getUser(id: number): Promise<BackendUser> {
    try {
      const response = await httpClient.get<UserResponse>(`/auth/users/${id}/`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user');
      }
    } catch (error: any) {
      console.error('Error fetching user:', error);
      
      // Handle response with success: false
      if (error.response?.data?.success === false) {
        throw new Error(error.response.data.message || 'Failed to fetch user');
      }
      
      // Handle other error formats
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      throw error;
    }
  },

  // Create new user
  async createUser(userData: CreateUserData): Promise<BackendUser> {
    try {
      const payload = {
        ...userData,
        site: userData.site || 'site1',
      };
      
      const response = await httpClient.post<UserResponse>('/auth/users/create/', payload);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create user');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Handle response with success: false
      if (error.response?.data?.success === false) {
        throw new Error(error.response.data.message || 'Failed to create user');
      }
      
      // Handle other error formats
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  // Update user
  async updateUser(id: number, userData: UpdateUserData): Promise<BackendUser> {
    try {
      const response = await httpClient.put<UserResponse>(`/auth/users/${id}/`, userData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update user');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Handle response with success: false
      if (error.response?.data?.success === false) {
        throw new Error(error.response.data.message || 'Failed to update user');
      }
      
      // Handle other error formats
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  // Activate user
  async activateUser(id: number): Promise<BackendUser> {
    return this.updateUser(id, { is_active: true });
  },

  // Deactivate user
  async deactivateUser(id: number): Promise<BackendUser> {
    return this.updateUser(id, { is_active: false });
  },

  // Change user password
  async changePassword(id: number, passwordData: ChangePasswordData): Promise<BackendUser> {
    try {
      const response = await httpClient.put<UserResponse>(`/auth/users/${id}/`, passwordData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to change password');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      
      // Handle response with success: false
      if (error.response?.data?.success === false) {
        throw new Error(error.response.data.message || 'Failed to change password');
      }
      
      // Handle other error formats
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },

  // Delete user
  async deleteUser(id: number): Promise<void> {
    try {
      const response = await httpClient.delete(`/auth/users/${id}/delete/`);
      
      // Check if response has success field
      if (response.data?.success === false) {
        throw new Error(response.data.message || 'Failed to delete user');
      }
      
      // For delete operations, we might not get a structured response
      if (response.status >= 200 && response.status < 300) {
        return;
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      
      // Handle response with success: false
      if (error.response?.data?.success === false) {
        throw new Error(error.response.data.message || 'Failed to delete user');
      }
      
      // Handle other error formats
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  },
};